import {
  type AlignedBlockTag,
  type BlockAlign,
  parseTextAlignStyle,
  readBlockAlign,
  serializeAlignedBlockClose,
  serializeAlignedBlockOpen,
  type TextAlign,
  textAlignStyle,
} from '@bangle.io/markdown-syntax';
import type { MarkdownSerializerState } from 'prosemirror-markdown';
import type { AttributeSpec, Command, EditorState, PMNode } from './pm';

export type { BlockAlign, TextAlign } from '@bangle.io/markdown-syntax';

/** Name of the node attribute that stores a block's alignment. */
export const TEXT_ALIGN_ATTR = 'align';

/**
 * Attribute spec shared by every block that supports alignment. `null` is the
 * default (left) so aligned-less documents serialize exactly as before.
 */
export const TEXT_ALIGN_ATTR_SPEC: Record<
  typeof TEXT_ALIGN_ATTR,
  AttributeSpec
> = {
  [TEXT_ALIGN_ATTR]: { default: null },
};

/** `parseDOM` helper: read a block's alignment from its inline `style`. */
export function parseTextAlignFromDOM(dom: string | HTMLElement): {
  align: BlockAlign | null;
} {
  if (typeof dom === 'string') {
    return { align: null };
  }
  return {
    align: readBlockAlign(parseTextAlignStyle(dom.getAttribute('style'))),
  };
}

/** `toDOM` helper: the DOM attrs for a block, empty for the default left. */
export function textAlignDOMAttrs(value: unknown): Record<string, string> {
  const align = readBlockAlign(value);
  return align ? { style: textAlignStyle(align) } : {};
}

/**
 * Markdown helper: write `render`'s inline content wrapped in an aligned HTML
 * tag, e.g. `<p style="text-align: center">…</p>`.
 *
 * Raw HTML is disabled when parsing, so a literal `<` in the text is escaped:
 * otherwise text that happens to read `</p>` would end the block early.
 */
export function writeAlignedBlock(
  state: MarkdownSerializerState,
  tag: AlignedBlockTag,
  align: BlockAlign,
  render: () => void,
): void {
  state.write(serializeAlignedBlockOpen(tag, align));
  const previous = state.options.escapeExtraCharacters;
  state.options.escapeExtraCharacters = previous
    ? new RegExp(`${previous.source}|<`, 'g')
    : /</g;
  try {
    render();
  } finally {
    state.options.escapeExtraCharacters = previous;
  }
  state.write(serializeAlignedBlockClose(tag));
}

function supportsTextAlign(node: PMNode): boolean {
  return node.isTextblock && TEXT_ALIGN_ATTR in node.attrs;
}

/** Positions of the blocks in the selection that can carry an alignment. */
function alignableBlocks(state: EditorState): { node: PMNode; pos: number }[] {
  const blocks: { node: PMNode; pos: number }[] = [];
  const { from, to } = state.selection;
  state.doc.nodesBetween(from, to, (node, pos) => {
    if (!node.isTextblock) {
      return true;
    }
    if (supportsTextAlign(node)) {
      blocks.push({ node, pos });
    }
    return false;
  });
  return blocks;
}

/**
 * Align every paragraph and heading in the selection. Only the alignment is
 * written: other attrs, such as a heading's level, are kept. `left` clears the
 * stored alignment. Without `dispatch` this is a dry run that reports whether
 * any block would change.
 */
export function setTextAlign(align: TextAlign): Command {
  const value = readBlockAlign(align);
  return (state, dispatch) => {
    const changed = alignableBlocks(state).filter(
      ({ node }) => readBlockAlign(node.attrs[TEXT_ALIGN_ATTR]) !== value,
    );
    if (changed.length === 0) {
      return false;
    }
    if (dispatch) {
      const tr = state.tr;
      for (const { node, pos } of changed) {
        tr.setNodeMarkup(pos, undefined, {
          ...node.attrs,
          [TEXT_ALIGN_ATTR]: value,
        });
      }
      dispatch(tr.scrollIntoView());
    }
    return true;
  };
}

/**
 * Whether every paragraph and heading in the selection has this alignment.
 * `false` for a selection with no alignable block, and for a mixed selection.
 */
export function isTextAlignActive(
  state: EditorState,
  align: TextAlign,
): boolean {
  const value = readBlockAlign(align);
  const blocks = alignableBlocks(state);
  return (
    blocks.length > 0 &&
    blocks.every(
      ({ node }) => readBlockAlign(node.attrs[TEXT_ALIGN_ATTR]) === value,
    )
  );
}
